import uuid from 'uuid/v4';
import _ from 'lodash';
import {
  numberWithCommas,
  isFeaturePresent,
  getStudyFromStudies,
  filterStudyList,
  checkWeightType,
  roundWeighted,
} from './CommonUtils';

import sampleWhiteIcon from '../images/sample-icon_white.png';
import weightedWhiteIcon from '../images/weighted-icon_white.png';
import verticalWhiteIcon from '../images/vertical-icon-down_white.png';
import horizontalWhiteIcon from '../images/horizontal-icon_white.png';
import indexWhiteIcon from '../images/index-icon_white.png';
import {
  fetchStandardBaseList,
  updateCrosstabGrid,
} from '../actions/crosstabActions';
import { setPreferenceFlag } from '../actions/preferencesActions';
import {
  PREFERENCE_CROSSTAB,
  PREFERENCE_CHART_VIEW,
  PREFERENCE_STUDY_WEIGHTING,
  PREFERENCE_DATAHAUL,
  PREFERENCE_STORIES,
  DATAHAUL_CONSTANT,
  VISAHAUL_CONSTANT,
  FLEX_STUDY_TAG,
  CROSSTAB_FEATUREID,
  MRI_STUDY_WARNING_TYPE_6,
  MRI_STUDY_WARNING_TYPE_7,
  MRI_STUDY_WARNING_TYPE_8,
  MIN_ROW_HEIGHT,
  MAX_ROW_TEXT_NUM,
  MIN_COLUMN_TEXT_HEIGHT,
  MIN_ROW_TEXT_HEIGHT,
  GRID_ROW_COL_MAX_TEXT,
  CROSSTAB_COLUMN_MIN_HEIGHT,
  CROSSTAB_COLUMN_TEXT_HEIGHT,
  MIN_COLUMN_HEIGHT,
  MAX_COLUMN_TEXT_NUM,
  MIN_COLUMN_TEXT_NUM,
  CROSSTAB_PROBABILITIES_NA_CONSTANT,
} from '../constants';

import { NIELSEN_TV_PATH_ID, NIELSEN_TV_TEXT_PATH } from '../actions/urlTypes';

import {
  buildAutoCodeObject,
  buildFunctionNode,
  mergeGeographyNodes,
  buildAutocodenodeWithNot,
  buildACStructureNode,
} from './autocodeUtils';
import {
  getExpressionFormed,
  getObjectAdded,
  checkKeywords,
  functionsInComposer,
  specialCharList,
} from '../components/autocode/AutocodeFunctions';

import { AC_OPERATORS } from '../components/autocode/AutocodeConstants';

import { GEOGRAPHY_BASES } from '../actions/actionTypes';

import AutocodeLabelsGenerator, {
  ACStandardLabels,
} from '../components/autocode/AutocodeLabelsGenerator';

const { toLabelString } = AutocodeLabelsGenerator(ACStandardLabels, null);

const stripToNumber = val => Number(val.replace(/[%*, ]/g, ''));

const handleDivideByZero = (num, den) => (num && den ? num / den : 0);

export const DEFAULT_CROSSTAB_DATA_KEYS = {
  Study_UNIVERSE: 'StudyUniverseKey',
};

export const SORT_DIRECTION = {
  NONE: 'none',
  ASC: 'asc',
  DESC: 'desc',
};

export const CROSSTAB_VIEWER_GRID_TYPE = {
  CROSSTAB: 'Crosstab View',
  PRIVATE_EYE: 'Private Eye View',
  TREND: 'Trend',
  QUADCHART: 'Quad',
  CORR_CLUSTER: 'Corr Cluster',
};

export const DEFAULT_CELL_ELEMENTS = [
  {
    key: 'sample',
    isVisible: true,
    isTrendVisibility: true,
    label: 'SAMPLE',
    text: 'SAMPLE',
    bgColorClass: 'sampleIcon',
    iconImage: sampleWhiteIcon,
    compute: stripToNumber,
    iconClass: 'sample-cell-icon',
  },
  {
    key: 'weighted',
    isVisible: true,
    isTrendVisibility: true,
    label: 'WEIGHTED (000)',
    text: 'WEIGHTED',
    bgColorClass: 'weightedIcon',
    iconImage: weightedWhiteIcon,
    compute: stripToNumber,
    iconClass: 'weighted-cell-icon',
  },
  {
    key: 'vertical',
    isVisible: true,
    isTrendVisibility: true,
    label: 'VERTICAL %',
    text: 'VERTICAL',
    bgColorClass: 'verticalIcon',
    iconImage: verticalWhiteIcon,
    compute: stripToNumber,
    iconClass: 'vertical-cell-icon',
  },
  {
    key: 'horizontal',
    isVisible: true,
    isTrendVisibility: false,
    label: 'HORIZONTAL %',
    text: 'HORIZONTAL',
    bgColorClass: 'horizontalIcon',
    iconImage: horizontalWhiteIcon,
    compute: stripToNumber,
    iconClass: 'horizontal-cell-icon',
  },
  {
    key: 'index',
    isVisible: true,
    isTrendVisibility: false,
    label: 'INDEX',
    text: 'INDEX',
    bgColorClass: 'indexIcon',
    iconImage: indexWhiteIcon,
    compute: stripToNumber,
    iconClass: 'index-cell-icon',
  },
];

export const getDefaultCells = crosstabWeightTypes => {
  const NEW_CELL_ELEMENTS = [...DEFAULT_CELL_ELEMENTS];
  const MODIFIED_CELL_ELEMENTS = NEW_CELL_ELEMENTS.map(cell => {
    const cellElement = cell;
    if (cell.key === 'weighted') {
      cellElement.label = `WEIGHTED${checkWeightType(crosstabWeightTypes)}`;
    }
    return cellElement;
  });
  return MODIFIED_CELL_ELEMENTS;
};

export const DEFAULT_GRID_CELL_LABEL = '*USER_OR_SYSTEM_MODIFIED_LABEL*';
export const NOT_VALID_LABEL = 'Not Valid';

export const smartRoundOff = value => {
  if (value < 1) {
    return Math.round(value * 100) / 100;
  }

  if (value < 100) {
    return Math.round(value * 10) / 10;
  }

  return Math.round(value);
};

const computePercent = (numerator, denominator, roundOff) => {
  const result = denominator ? (numerator / denominator) * 100 : 0;
  if (result === 'N/A') return result;
  if (denominator === 'N/A') {
    return 'N/A';
  }
  return roundOff
    ? Math.round(result * Math.pow(10, roundOff)) / Math.pow(10, roundOff)
    : Math.round(result);
};

const customCrosstabComparator = (
  sortColumnIndex,
  sortKey,
  sortOrder,
  primer
) => {
  const getCellMetrics = primer
    ? row => primer(row.cellList[sortColumnIndex][sortKey])
    : row => row.cellList[sortColumnIndex][sortKey];
  const sortOrderNumber = sortOrder === SORT_DIRECTION.ASC ? 1 : -1;

  return (a, b) => {
    const el1 = getCellMetrics(a);
    const el2 = getCellMetrics(b);
    if (isNaN(el1)) {
      return 1;
    }
    if (isNaN(el2)) {
      return -1;
    }
    return sortOrderNumber * ((el1 > el2) - (el2 > el1));
  };
};

export const utilExportHighlightObject = (item, element, type) => {
  let operator = '';
  let valueStart = item.value;
  let valueEnd = '';
  let arrRangeValue = [];
  if (item.operator === 'Less Than') {
    operator = 'lt';
  } else if (item.operator === 'Greater Than') {
    operator = 'gt';
  } else if (item.operator === 'Equals') {
    operator = 'eq';
  } else if (item.operator === 'Range') {
    operator = 'range';
    arrRangeValue = item.value.split(' to ');
    valueStart = arrRangeValue[0];
    valueEnd = arrRangeValue[1];
  }
  return {
    element,
    operator,
    type,
    textColor: item.textColor || '',
    cellColor: item.cellColor || '',
    cellElement: '',
    valueStart,
    valueEnd,
  };
};
export const sortCrosstabTable = (crosstabTable, crosstabWeightTypes) => {
  const sortColumnIndex = crosstabTable.sortColumnIndex;
  const sortKey = crosstabTable.sortKey;
  const sortOrder = crosstabTable.sortOrder;
  const primer = getDefaultCells(crosstabWeightTypes).filter(
    cellElement => cellElement.key === sortKey
  )[0].compute;

  const newRowSubList = crosstabTable.crosstabPerStudy[0][
    crosstabTable.selectedBase
  ].rowList.slice(1);

  const rowList = [
    crosstabTable.crosstabPerStudy[0][crosstabTable.selectedBase].rowList[0],
    ...newRowSubList.sort(
      customCrosstabComparator(sortColumnIndex, sortKey, sortOrder, primer)
    ),
  ];

  const crosstabAtBaseStudy = crosstabTable.crosstabPerStudy[0].map(
    (crosstab, index) => {
      if (index === crosstabTable.selectedBase) {
        return {
          ...crosstab,
          rowList,
        };
      }
      return crosstab;
    }
  );

  return {
    ...crosstabTable,
    crosstabPerStudy: [
      crosstabAtBaseStudy,
      ...crosstabTable.crosstabPerStudy.slice(1),
    ],
  };
};

export const getStudyGroup = (studyType, studies) => {
  let studyGroup;
  studies.forEach(studyGroups => {
    if (_.find(studyGroups.sl, type => type.sti === studyType)) {
      studyGroup = studyGroups;
    }
  });
  return studyGroup;
};

export const filterLabel = (title, breadcrumbs) => {
  const breadcrumb = breadcrumbs.split(' > ');

  if (breadcrumb[0] && breadcrumb[0].toUpperCase() === 'DEFINITIONS') {
    return `${title}`;
  }

  return `${title}: ${breadcrumbs}`;
};

export const populateCrosstabData = ({
  crosstabData,
  crosstabTitle,
  studyDescriptions,
  colExpressionList,
  rowExpressionList,
  baseList,
  colHeaderList,
  rowHeaderList,
  baseHeaderList,
  studyInfo,
  baseIDList,
  title,
  id,
  runType,
  highlightAndFilter,
  conditionalFormatting,
  crosstabTitleHeaderList,
  userDefinedBasesPresent,
  selectedBaseNode,
  selectedBaseGroup,
  selectedBaseID,
  userDefinedBaseList,
  standardBaseList,
  accountId,
  crosstabColumns,
  crosstabRows,
}) => {
  let isLabelUpdated = false;
  let isNADataPresent = false;
  let isProbabilitiesPresent = false;
  const tableData = {
    idKey: uuid(),
    viewerGridType: runType,
    selectedDropdownColumn: 0,
    isActive: true,
    crosstabTitle,
    rowExpressionList,
    colExpressionList,
    colHeaderList,
    conditionalFormatting,
    highlightAndFilter,
    rowHeaderList,
    baseHeaderList,
    baseList,
    studyInfo,
    baseIDList,
    title,
    selectedBaseNode,
    selectedBaseGroup,
    selectedBaseID,
    userDefinedBaseList,
    standardBaseList,
    id,
    columnHeaderList: crosstabData[0].headerList.map((label, index) => {
      if (index === 0) {
        return { label };
      }
      if (crosstabColumns[index - 1].isLabelUpdated) {
        isLabelUpdated = true;
      }
      const columnHeader = crosstabColumns[index - 1].isLabelUpdated
        ? { label, isColumnLabelUpdated: true }
        : { label };
      return columnHeader;
    }),
    crosstabPerStudy: [],
  };

  const colHierarchy = [];
  const autocodenodeColHierarchy = [];
  crosstabColumns.forEach(objs => {
    const objAdded = objs.dataSmartSearch
      ? objs.dataSmartSearch.objectsAdded
      : [];
    if (objAdded.length > 0) {
      if (objAdded.length === 1 && objAdded[0].breadcrumbs) {
        const label = objAdded[0].dataSmartSearchObjectsCreated
          ? objAdded[0].breadcrumbs
          : filterLabel(
              `${
                objAdded[0].shortTextCatgView
                  ? objAdded[0].shortTextCatgView
                  : objAdded[0].title
              }`,
              `${objAdded[0].breadcrumbs}`
            );
        colHierarchy.push(label);
      } else {
        let longText = '';
        objAdded.forEach(item => {
          let textValue = '';
          if (item.breadcrumbs) {
            // textValue = `${item.shortTextCatgView}: ${item.breadcrumbs}`;
            textValue = filterLabel(item.shortTextCatgView, item.breadcrumbs);
          } else if (item.objectType === 'operator') {
            textValue = `${textValue} ${item.shortText} `;
          }
          longText += textValue;
        });
        colHierarchy.push(longText);
      }
    }

    if (objs.dataSmartSearch && objs.dataSmartSearch.autocodenode) {
      autocodenodeColHierarchy.push(objs.dataSmartSearch.autocodenode);
    }
  });

  const rowHierarchy = [];
  const autocodenodeRowHierarchy = [];
  crosstabRows.forEach(objs => {
    const objAdded = objs.dataSmartSearch
      ? objs.dataSmartSearch.objectsAdded
      : [];
    if (objAdded.length > 0) {
      if (objAdded.length === 1 && objAdded[0].breadcrumbs) {
        const label = objAdded[0].dataSmartSearchObjectsCreated
          ? objAdded[0].breadcrumbs
          : filterLabel(
              `${
                objAdded[0].shortTextCatgView
                  ? objAdded[0].shortTextCatgView
                  : objAdded[0].title
              }`,
              `${objAdded[0].breadcrumbs}`
            );
        rowHierarchy.push(label);
      } else {
        let longText = '';
        objAdded.forEach(item => {
          let textValue = '';
          if (item.breadcrumbs) {
            //  textValue = `${item.shortTextCatgView}: ${item.breadcrumbs}`;
            textValue = filterLabel(item.shortTextCatgView, item.breadcrumbs);
          } else if (item.objectType === 'operator') {
            textValue = `${textValue} ${item.shortText} `;
          }
          longText += textValue;
        });
        rowHierarchy.push(longText);
      }
    }
    if (objs.dataSmartSearch && objs.dataSmartSearch.autocodenode) {
      autocodenodeRowHierarchy.push(objs.dataSmartSearch.autocodenode);
    }
  });

  tableData.columnHeaderList.map((item, index) => {
    if (index !== 0 && !item.tooltipVal) {
      if (colHierarchy.length > 0) {
        item.tooltipVal = colHierarchy[index - 1];
      }
      if (autocodenodeColHierarchy.length > 0) {
        item.autocodenode = autocodenodeColHierarchy[index - 1];
      }
    }
    if (index !== 0) {
      item.footnotes = crosstabData[0].headerFootNotes[index - 1];
    }
    return item;
  });

  const isLocalOrKidsStudy =
    studyInfo.sd.includes('Kids') ||
    studyInfo.sd.includes('SimmonsLOCAL') ||
    (studyInfo.sd.includes('LGBT') && accountId !== 5261);

  const isMRISpecificStudy = studyInfo.ss.includes('MRI');
  const warningType = studyInfo.wnt;

  tableData.crosstabPerStudy = crosstabData.map(
    (crosstabPerStudyData, studyIndex) => {
      if (
        crosstabPerStudyData &&
        crosstabPerStudyData.crosstabList &&
        crosstabPerStudyData.crosstabList.length > 0
      ) {
        return crosstabPerStudyData.crosstabList.map((crosstab, baseIndex) => {
          const { filter, filtered, rowList } = crosstab;
          const productNo = crosstab.productNo;
          // as baseList does not have study universe, so subtract 1.
          const baseId = !studyInfo.st.toLowerCase().includes(FLEX_STUDY_TAG)
            ? baseIDList[baseIndex - 1]
            : baseIDList[baseIndex];
          const studyId =
            baseIndex === 0 &&
            !studyInfo.st.toLowerCase().includes(FLEX_STUDY_TAG)
              ? DEFAULT_CROSSTAB_DATA_KEYS.Study_UNIVERSE
              : baseId;
          const perBaseReturnData = {
            idKey: studyId,
            isCondition1: false,
            isCondition2: false,
            isMRICondition1: false,
            studyDescription: studyDescriptions[studyIndex],
            productNo,
            filter,
            filtered,
          };

          if (
            filter === DEFAULT_GRID_CELL_LABEL ||
            (crosstabTitleHeaderList &&
              crosstabTitleHeaderList.BasesDataGrid &&
              crosstabTitleHeaderList.BasesDataGrid[filter])
          ) {
            isLabelUpdated = true;
            perBaseReturnData.isBaseLabelUpdated = true;
          }

          if (
            rowList &&
            rowList.length > 0 &&
            rowList[0].cellList &&
            rowList[0].cellList.length > 0
          ) {
            const baseCell = {
              ...rowList[0].cellList[0],
              vertical: 100,
              horizontal: 100,
              index: 100,
            };

            perBaseReturnData.rowList = rowList.map((row, index) => {
              let baseHorizontalWeight = null;
              let isRowLabelUpdated = false;
              let baseComputedVerticalExact = null;
              if (row.cellList[0]) {
                baseHorizontalWeight = row.cellList[0].weighted;
                baseComputedVerticalExact = handleDivideByZero(
                  baseHorizontalWeight,
                  baseCell.weighted
                );
                if (index !== 0) {
                  if (crosstabRows[index - 1].isLabelUpdated) {
                    isRowLabelUpdated = true;
                    isLabelUpdated = true;
                  }
                }
              }
              if (index !== 0 && !row.tooltipVal) {
                if (rowHierarchy.length > 0) {
                  row.tooltipVal = rowHierarchy[index - 1];
                }
                if (autocodenodeRowHierarchy.length > 0) {
                  row.autocodenode = autocodenodeRowHierarchy[index - 1];
                }
              }

              const cellList = row.cellList.map((cell, cellIndex) => {
                if (!cell) {
                  isNADataPresent = true;
                  const computedCell = { weighted: 'N/A', sample: 'N/A' };
                  computedCell.sample = 'N/A';
                  computedCell.weighted = 'N/A';
                  computedCell.vertical = 'N/A';
                  computedCell.horizontal = 'N/A';
                  computedCell.index = 'N/A';
                  return computedCell;
                }
                if (cell && cell.piv) {
                  isProbabilitiesPresent = true;
                  const computedCell = {
                    weighted: CROSSTAB_PROBABILITIES_NA_CONSTANT,
                    sample: CROSSTAB_PROBABILITIES_NA_CONSTANT,
                  };
                  computedCell.sample = CROSSTAB_PROBABILITIES_NA_CONSTANT;
                  computedCell.weighted = CROSSTAB_PROBABILITIES_NA_CONSTANT;
                  computedCell.vertical = CROSSTAB_PROBABILITIES_NA_CONSTANT;
                  computedCell.horizontal = CROSSTAB_PROBABILITIES_NA_CONSTANT;
                  computedCell.index = CROSSTAB_PROBABILITIES_NA_CONSTANT;
                  computedCell.piv = true;
                  computedCell.rowData = crosstabRows[index - 1];
                  computedCell.colData = crosstabColumns[cellIndex - 1];
                  return computedCell;
                }
                
                if (cell.numeric) {
                  const computedCell = {
                    weighted: numberWithCommas(
                      roundWeighted(cell.weighted)
                    ),
                    sample: 'N/A',
                    vertical: 'N/A',
                    horizontal: 'N/A',
                  }
                  let index = 0;
                  if (row.cellList[0] && row.cellList[0].numeric && row.cellList[0].weighted > 0) {
                    index = computePercent(roundWeighted(cell.weighted), roundWeighted(row.cellList[0].weighted));
                  } else if (rowList[0].cellList[cellIndex] && rowList[0].cellList[cellIndex].numeric &&
                      rowList[0].cellList[cellIndex].weighted > 0){
                    index = computePercent(roundWeighted(cell.weighted),
                      roundWeighted(rowList[0].cellList[cellIndex].weighted));
                  } else {
                    index = 'N/A';
                  }
                  computedCell.index = index;
                  if (computedCell.index > 110) {
                    computedCell.indexClassName = 'up-arrow-index-icon';
                  } else if (
                    computedCell.index < 90 &&
                    computedCell.index !== 0
                  ) {
                    computedCell.indexClassName = 'down-arrow-index-icon';
                  } else if (
                    computedCell.index >= 90 &&
                    computedCell.index <= 110
                  ) {
                    computedCell.indexClassName = 'flat-arrow-index-icon';
                  }
                  if (isNaN(computedCell.index)) {
                    computedCell.index = 'N/A';
                  }
                  computedCell.index =
                    computedCell.index !== 'N/A'
                      ? `${numberWithCommas(smartRoundOff(computedCell.index))}`
                      : 'N/A';
                  return computedCell;
                }
                const baseVerticalWeight = rowList[0].cellList[cellIndex]
                  ? rowList[0].cellList[cellIndex].weighted
                  : 'N/A';
                const baseSampleWeight = rowList[0].cellList[cellIndex]
                  ? rowList[0].cellList[cellIndex].intab
                  : 'N/A';
                if (
                  baseVerticalWeight === 'N/A' ||
                  baseSampleWeight === 'N/A'
                ) {
                  isNADataPresent = true;
                }
                const computedCell = {
                  weighted: cell.weighted,
                  sample: cell.intab,
                };
                computedCell.horizontal = computePercent(
                  computedCell.weighted,
                  baseHorizontalWeight,
                  1
                );
                computedCell.vertical = computePercent(
                  computedCell.weighted,
                  baseVerticalWeight,
                  1
                );
                const verticalExact = handleDivideByZero(
                  computedCell.weighted,
                  baseVerticalWeight
                );
                computedCell.index =
                  baseSampleWeight === 'N/A' || baseHorizontalWeight === null
                    ? 'N/A'
                    : computePercent(verticalExact, baseComputedVerticalExact);
                computedCell.samplePerc = computePercent(
                  computedCell.sample,
                  baseSampleWeight
                );
                computedCell.weightedPerc = computePercent(
                  computedCell.weighted,
                  baseVerticalWeight
                );
                if (computedCell.index > 110) {
                  computedCell.indexClassName = 'up-arrow-index-icon';
                } else if (
                  computedCell.index < 90 &&
                  computedCell.index !== 0
                ) {
                  computedCell.indexClassName = 'down-arrow-index-icon';
                } else if (
                  computedCell.index >= 90 &&
                  computedCell.index <= 110
                ) {
                  computedCell.indexClassName = 'flat-arrow-index-icon';
                }
                computedCell.sample = numberWithCommas(
                  Math.round(computedCell.sample)
                );
                computedCell.weighted = numberWithCommas(
                  roundWeighted(computedCell.weighted)
                );
                computedCell.vertical =
                  computedCell.vertical !== 'N/A'
                    ? `${computedCell.vertical}%`
                    : 'N/A';
                computedCell.horizontal =
                  computedCell.horizontal === 'N/A' ||
                  baseHorizontalWeight === null
                    ? 'N/A'
                    : `${numberWithCommas(
                        smartRoundOff(computedCell.horizontal)
                      )}%`;
                if (isNaN(computedCell.index)) {
                  computedCell.index = 'N/A';
                }
                computedCell.index =
                  computedCell.index !== 'N/A'
                    ? `${numberWithCommas(smartRoundOff(computedCell.index))}`
                    : 'N/A';

                if (!isLocalOrKidsStudy && !isMRISpecificStudy) {
                  if (stripToNumber(computedCell.sample) < 31) {
                    perBaseReturnData.isCondition2 = true;
                    computedCell.sample = `** ${computedCell.sample}`;
                    computedCell.weighted = `** ${computedCell.weighted}`;
                    computedCell.vertical = `** ${computedCell.vertical}`;
                    computedCell.horizontal = `** ${computedCell.horizontal}`;
                    computedCell.index = `** ${computedCell.index}`;
                  } else if (stripToNumber(computedCell.sample) < 61) {
                    perBaseReturnData.isCondition1 = true;
                    computedCell.sample = `* ${computedCell.sample}`;
                    computedCell.weighted = `* ${computedCell.weighted}`;
                    computedCell.vertical = `* ${computedCell.vertical}`;
                    computedCell.horizontal = `* ${computedCell.horizontal}`;
                    computedCell.index = `* ${computedCell.index}`;
                  }
                }

                if (isMRISpecificStudy) {
                  if (
                    warningType === MRI_STUDY_WARNING_TYPE_6 &&
                    stripToNumber(computedCell.sample) < 50
                  ) {
                    perBaseReturnData.isMRICondition1 = true;
                    computedCell.sample = `* ${computedCell.sample}`;
                    computedCell.weighted = `* ${computedCell.weighted}`;
                    computedCell.vertical = `* ${computedCell.vertical}`;
                    computedCell.horizontal = `* ${computedCell.horizontal}`;
                    computedCell.index = `* ${computedCell.index}`;
                  } else if (warningType === MRI_STUDY_WARNING_TYPE_7) {
                    if (stripToNumber(computedCell.sample) < 25) {
                      perBaseReturnData.isMRICondition2 = true;
                      computedCell.sample = `** ${computedCell.sample}`;
                      computedCell.weighted = `** ${computedCell.weighted}`;
                      computedCell.vertical = `** ${computedCell.vertical}`;
                      computedCell.horizontal = `** ${computedCell.horizontal}`;
                      computedCell.index = `** ${computedCell.index}`;
                    } else if (stripToNumber(computedCell.sample) < 50) {
                      perBaseReturnData.isMRICondition3 = true;
                      computedCell.sample = `* ${computedCell.sample}`;
                      computedCell.weighted = `* ${computedCell.weighted}`;
                      computedCell.vertical = `* ${computedCell.vertical}`;
                      computedCell.horizontal = `* ${computedCell.horizontal}`;
                      computedCell.index = `* ${computedCell.index}`;
                    }
                  } else if (warningType === MRI_STUDY_WARNING_TYPE_8) {
                    if (stripToNumber(computedCell.sample) < 31) {
                      perBaseReturnData.isMRICondition4 = true;
                      computedCell.sample = `** ${computedCell.sample}`;
                      computedCell.weighted = `** ${computedCell.weighted}`;
                      computedCell.vertical = `** ${computedCell.vertical}`;
                      computedCell.horizontal = `** ${computedCell.horizontal}`;
                      computedCell.index = `** ${computedCell.index}`;
                    } else if (stripToNumber(computedCell.sample) < 60) {
                      perBaseReturnData.isMRICondition5 = true;
                      computedCell.sample = `* ${computedCell.sample}`;
                      computedCell.weighted = `* ${computedCell.weighted}`;
                      computedCell.vertical = `* ${computedCell.vertical}`;
                      computedCell.horizontal = `* ${computedCell.horizontal}`;
                      computedCell.index = `* ${computedCell.index}`;
                    }
                  }
                }
                return computedCell;
              });
              return {
                rowHeader: row.text,
                isRowLabelUpdated,
                cellList,
                tooltipVal: row.tooltipVal,
                footnotes: row.footnotes,
                autocodenode: row.autocodenode,
              };
            });
          }
          return perBaseReturnData;
        });
      }
      return undefined;
    }
  );

  tableData.isLabelUpdated = isLabelUpdated;
  tableData.isNADataPresent = isNADataPresent;
  tableData.isProbabilitiesPresent = isProbabilitiesPresent;
  const crosstabDefaultStudy = tableData.crosstabPerStudy[0];
  if (
    !userDefinedBasesPresent ||
    tableData.studyInfo.st.toLowerCase().includes(FLEX_STUDY_TAG)
  ) {
    if (tableData.selectedBaseGroup === 'standard-base') {
      let selectedStandardBaseID;
      let selectedBaseName;

      standardBaseList.map(elems => {
        if (tableData.selectedBaseNode === elems.keyword) {
          selectedStandardBaseID = elems.title;
          selectedBaseName = elems.keyword;
        }
        return null;
      });
      crosstabDefaultStudy.forEach((elems, index) => {
        if (elems.filter === selectedStandardBaseID) {
          tableData.selectedBase = index;
        }
      });
      if (tableData.selectedBase !== undefined) {
        tableData.selectedBaseName = selectedBaseName;
      } else {
        tableData.selectedBase = 0;
        tableData.selectedBaseName = crosstabDefaultStudy[0].filter;
      }
    } else {
      tableData.selectedBase = 0;
      tableData.selectedBaseName = crosstabDefaultStudy[0].filter;
    }
  } else if (userDefinedBasesPresent) {
    if (tableData.selectedBaseGroup === 'standard-base') {
      let selectedStandardBaseID;
      let selectedBaseName;

      standardBaseList.map(elems => {
        if (tableData.selectedBaseNode === elems.keyword) {
          selectedStandardBaseID = elems.title;
          selectedBaseName = elems.keyword;
        }
        return null;
      });
      crosstabDefaultStudy.forEach((elems, index) => {
        if (elems.filter === selectedStandardBaseID) {
          tableData.selectedBase = index;
        }
      });

      if (tableData.selectedBase !== undefined) {
        tableData.selectedBaseName = selectedBaseName;
      } else {
        tableData.selectedBase = 1;
        tableData.selectedBaseName = crosstabDefaultStudy[1].filter;
      }
    } else if (tableData.selectedBaseGroup === 'user-defined') {
      tableData.selectedBase =
        tableData.baseIDList.indexOf(tableData.selectedBaseID) + 1;
      tableData.selectedBaseName =
        crosstabDefaultStudy[
          tableData.baseIDList.indexOf(tableData.selectedBaseID) + 1
        ].filter;
    } else {
      tableData.selectedBase = 0;
      tableData.selectedBaseName = crosstabDefaultStudy[0].filter;
    }
  }
  return tableData;
};

export const getMidpointValue = (keyword, data) => {
  return data.find(item => item.keyword === keyword) || 0;
};

export const setCrosstabDataOnSave = (preferences, dispatch, getState) => {
  const { study, login, account, crosstab, userDetail } = getState();
  let hasFeature = false;
  if (userDetail.permissions.length > 0) {
    hasFeature = isFeaturePresent(CROSSTAB_FEATUREID, userDetail.permissions);
  }
  if (
    study.studies !== undefined &&
    study.studies.length > 0 &&
    !_.isEmpty(preferences[PREFERENCE_CROSSTAB]) &&
    preferences[PREFERENCE_CROSSTAB].modified
  ) {
    let selectedStudy = {};
    const updatedWeightType =
      preferences[PREFERENCE_CROSSTAB][PREFERENCE_STUDY_WEIGHTING] ===
      'Population'
        ? 1
        : 2;
    if (preferences[PREFERENCE_CROSSTAB]._ds !== -1) {
      selectedStudy = getStudyFromStudies(
        filterStudyList(study.studies, CROSSTAB_FEATUREID),
        preferences[PREFERENCE_CROSSTAB]._ds
      );
      selectedStudy = {
        ...selectedStudy,
        wt: updatedWeightType,
      };
    } else {
      selectedStudy = study.studies[0].sl[0];
      selectedStudy = {
        ...selectedStudy,
        wt: updatedWeightType,
      };
    }
    const fetchStandardBasetParams = {
      token: login.loginResponse.data.token,
      uid: login.loginResponse.data.uid,
      accountId: account.activeAccountId,
      productNo: selectedStudy.pn,
    };
    if (!login.loginResponse.data.universityUser && hasFeature) {
      dispatch(fetchStandardBaseList(fetchStandardBasetParams));
    }
    dispatch(setPreferenceFlag('crosstab', true));
    if (
      crosstab.crosstabTableList &&
      crosstab.crosstabTableList.length > 0 &&
      preferences[PREFERENCE_CROSSTAB][PREFERENCE_CHART_VIEW] !==
        crosstab.crosstabTableList[0].viewerGridType &&
      preferences[PREFERENCE_CROSSTAB].modified
    ) {
      const viewerGridType =
        preferences[PREFERENCE_CROSSTAB][PREFERENCE_CHART_VIEW];
      dispatch(
        updateCrosstabGrid(crosstab.crosstabTableList[0].idKey, {
          viewerGridType,
        })
      );
    }
  }
  if (
    study.studies !== undefined &&
    study.studies.length > 0 &&
    !_.isEmpty(preferences[PREFERENCE_DATAHAUL]) &&
    preferences[PREFERENCE_DATAHAUL].modified
  ) {
    dispatch(setPreferenceFlag(DATAHAUL_CONSTANT, true));
  }
  if (
    study.studies !== undefined &&
    study.studies.length > 0 &&
    !_.isEmpty(preferences[PREFERENCE_STORIES]) &&
    preferences[PREFERENCE_STORIES].modified
  ) {
    dispatch(setPreferenceFlag(VISAHAUL_CONSTANT, true));
  }
};

export const isNielsenStudyPresent = data => {
  let nielsenPresent = false;
  if (typeof data !== 'string' && data.constructor === Array) {
    nielsenPresent = data.some(node => {
      if (node.pathId) {
        const shortTextArray = node.pathId.split('.');
        if (shortTextArray.indexOf(NIELSEN_TV_PATH_ID) !== -1) {
          return true;
        }
      } else if (node.textPath) {
        const shortTextArray = node.textPath.split('.');
        if (shortTextArray.indexOf(NIELSEN_TV_TEXT_PATH) !== -1) {
          return true;
        }
      }
      return false;
    });
  } else if (typeof data === 'string') {
    const shortTextArray = data.split('.');
    if (shortTextArray.indexOf(NIELSEN_TV_PATH_ID) !== -1) {
      nielsenPresent = true;
    }
  }
  return nielsenPresent;
};

export const getProcessedFilter = data => {
  let sampleFilter = [];
  let sampleHighlight = [];
  let weightedHighlight = [];
  let weightedFilter = [];
  let verticalHighlight = [];
  let verticalFilter = [];
  let horizontalHighlight = [];
  let horizontalFilter = [];
  let indexHighlight = [];
  let indexFilter = [];
  let sampleUpdated = false;
  let weightedUpdated = false;
  let verticalUpdated = false;
  let horizontalUpdated = false;
  let indexUpdated = false;
  let styleUpdated = false;
  if (data) {
    const { sample, weighted, vertical, horizontal, index } = data;
    if (sample.highlight.length > 0 || sample.filter.length > 0) {
      sampleHighlight = sample.highlight;
      sampleFilter = sample.filter;
      sampleUpdated = true;
    }
    if (weighted.highlight.length > 0 || weighted.filter.length > 0) {
      weightedHighlight = weighted.highlight;
      weightedFilter = weighted.filter;
      weightedUpdated = true;
    }
    if (vertical.highlight.length > 0 || vertical.filter.length > 0) {
      verticalHighlight = vertical.highlight;
      verticalFilter = vertical.filter;
      verticalUpdated = true;
    }
    if (horizontal.highlight.length > 0 || horizontal.filter.length > 0) {
      horizontalHighlight = horizontal.highlight;
      horizontalFilter = horizontal.filter;
      horizontalUpdated = true;
    }
    if (index.highlight.length > 0 || index.filter.length > 0) {
      indexHighlight = index.highlight;
      indexFilter = index.filter;
      indexUpdated = true;
    }

    if (
      sampleUpdated ||
      weightedUpdated ||
      verticalUpdated ||
      horizontalUpdated ||
      indexUpdated
    ) {
      styleUpdated = true;
    }
    return {
      sampleHighlight,
      sampleFilter,
      weightedHighlight,
      weightedFilter,
      verticalHighlight,
      verticalFilter,
      horizontalHighlight,
      horizontalFilter,
      indexHighlight,
      indexFilter,
      styleUpdated,
    };
  }
  return {};
};

export const singleDeleteOperation = (
  gridType,
  type,
  elemIndex,
  crosstabTableList,
  crosstabRequestData,
  crosstabPayload
) => {
  const updatedCrosstabTableList = crosstabTableList
    ? _.cloneDeep(crosstabTableList)
    : [];
  const updatedCrosstabRequestData = crosstabRequestData
    ? _.cloneDeep(crosstabRequestData)
    : {};
  const updatedCrosstabPayload = crosstabPayload
    ? _.cloneDeep(crosstabPayload)
    : [];
  // if col to delete is a sorted column, delete sort info
  if (type === 'col' && elemIndex === crosstabTableList[0].sortColumnIndex) {
    delete updatedCrosstabTableList[0].sortKey;
    delete updatedCrosstabTableList[0].sortColumnIndex;
    delete updatedCrosstabTableList[0].sortOrder;
  }
  if (type === 'row') {
    updatedCrosstabTableList[0].rowHeaderList.splice(elemIndex - 1, 1);
    updatedCrosstabTableList[0].rowExpressionList.splice(elemIndex - 1, 1);
    updatedCrosstabRequestData.rowHeaderList.splice(elemIndex - 1, 1);
    updatedCrosstabRequestData.rowExpressionList.splice(elemIndex - 1, 1);
    updatedCrosstabPayload[0].crosstabList.map(item => {
      item.rowList.splice(elemIndex, 1);
      return item;
    });
    if (updatedCrosstabTableList && updatedCrosstabTableList[0]) {
      updatedCrosstabTableList[0].crosstabPerStudy[0].map(item => {
        item.rowList.splice(elemIndex, 1);
        return item;
      });
    }
  } else {
    updatedCrosstabTableList[0].colHeaderList.splice(elemIndex - 1, 1);
    updatedCrosstabTableList[0].colExpressionList.splice(elemIndex - 1, 1);
    updatedCrosstabRequestData.colHeaderList.splice(elemIndex - 1, 1);
    updatedCrosstabRequestData.colExpressionList.splice(elemIndex - 1, 1);
    updatedCrosstabTableList[0].columnHeaderList.splice(elemIndex, 1);
    if (updatedCrosstabPayload && updatedCrosstabPayload[0]) {
      updatedCrosstabPayload[0].crosstabList.map(item => {
        item.rowList.map(cellItem => {
          cellItem.cellList.splice(elemIndex, 1);
          return cellItem;
        });
        return item;
      });
    }
    if (updatedCrosstabPayload && updatedCrosstabPayload[0]) {
      updatedCrosstabPayload[0].headerList.splice(elemIndex, 1);
    }
    if (updatedCrosstabTableList && updatedCrosstabTableList[0]) {
      updatedCrosstabTableList[0].crosstabPerStudy[0].map(item => {
        item.rowList.map(cellItem => {
          cellItem.cellList.splice(elemIndex, 1);
          return cellItem;
        });
        return item;
      });
    }
  }
  return {
    updatedCrosstabTableList,
    updatedCrosstabRequestData,
    updatedCrosstabPayload,
  };
};

export const crosstabViewTextEllipsis = (gridType, label) => {
  label = label.trim();
  if (gridType !== 'BasesDataGrid' && label.length > GRID_ROW_COL_MAX_TEXT) {
    label = label.slice(0, GRID_ROW_COL_MAX_TEXT);
  }
  return label;
};

export const shortLabelText = label => {
  return `${label.slice(0, 13)}...${label.slice(
    label.length - 10,
    label.length
  )}`;
};

// export const checkColRowLabels = (header, labelUpdatedFlag, gridType, toggleCheckVal) => {
//   let label = '';
//   if (gridType === 'column') {
//     label = labelUpdatedFlag || !header.tooltipVal ? header.label : header.tooltipVal;
//   } else {
//     label = labelUpdatedFlag || !header.tooltipVal ? header.rowHeader : header.tooltipVal;
//   }
//   const headerText = toggleCheckVal[gridType] ? crosstabViewTextEllipsis(gridType, label) : shortLabelText(label);
//   return headerText;
// };

const checkColRowLabels = (
  partsToShow,
  header,
  labelUpdatedFlag,
  gridType,
  toggleCheckVal
) => {
  let label = '';
  if (labelUpdatedFlag) {
    label = gridType === 'column' ? header.label : header.rowHeader;
  } else if (header.autocodenode) {
    label = toLabelString(header.autocodenode, partsToShow);
  } else if (gridType === 'column') {
    label =
      labelUpdatedFlag || !header.tooltipVal ? header.label : header.tooltipVal;
  } else {
    label =
      labelUpdatedFlag || !header.tooltipVal
        ? header.rowHeader
        : header.tooltipVal;
  }
  const headerText = toggleCheckVal[gridType]
    ? crosstabViewTextEllipsis(gridType, label)
    : shortLabelText(label);
  return headerText;
};

export const getNumLines = (
  partsToShow,
  headerVal,
  type,
  toggleCheckVal,
  charNum,
  labelUpdatedFlag
) => {
  let str = checkColRowLabels(
    partsToShow,
    headerVal,
    labelUpdatedFlag,
    type,
    toggleCheckVal
  );
  str = [...str];

  let num = 0;
  let newStr = '';
  let words = '';
  const stringLength = str.length - 1;
  str.forEach((chars, index) => {
    if (chars === ' ') {
      const temp = `${newStr}${words}`;
      if (temp.length > charNum) {
        num += 1;
        newStr = `${words}${chars}`;
      } else if (temp.length === charNum) {
        num += 1;
        newStr = `${chars}`;
      } else {
        newStr = `${temp}${chars}`;
      }
      if (index === stringLength && newStr.length > 0) {
        num += 1;
      }
      words = '';
    } else {
      words = `${words}${chars}`;
      if (index === stringLength) {
        const temp = `${newStr}${words}`;
        if (temp.length > charNum) {
          num += 2;
        } else {
          num += 1;
        }
      }
    }
  });
  // columns in full mode with long labels (length >= 500) should have an extra line to fit
  num =
    type === 'column' &&
    toggleCheckVal.column &&
    str.length >= GRID_ROW_COL_MAX_TEXT
      ? (num += 2)
      : (num += 1);
  return num;
};

export const setHeightOfRows = (
  partsToShow,
  headerVal,
  index,
  toggleCheckVal,
  cell,
  metricsList
) => {
  let finalHeight = MIN_ROW_HEIGHT;
  let minHeight = MIN_ROW_HEIGHT;
  if (cell === 'Crosstab-View') {
    finalHeight = 0;
    minHeight = 0;
    if (metricsList && metricsList[0]) {
      finalHeight += 28;
      minHeight += 28;
    }
    if (metricsList && metricsList[1]) {
      finalHeight += 28;
      minHeight += 28;
    }
    if (metricsList && metricsList[2]) {
      finalHeight += 28;
      minHeight += 28;
    }
    if (metricsList && metricsList[3]) {
      finalHeight += 28;
      minHeight += 28;
    }
    if (metricsList && metricsList[4]) {
      finalHeight += 28;
      minHeight += 28;
    }
    finalHeight = finalHeight < 40 ? 40 : finalHeight;
    minHeight = minHeight < 40 ? 40 : minHeight;
  }

  // let finalHeight = cell === 'Crosstab-View' ? CROSSTAB_MIN_ROW_HEIGHT : MIN_ROW_HEIGHT;
  // const minHeight = cell === 'Crosstab-View' ? CROSSTAB_MIN_ROW_HEIGHT : MIN_ROW_HEIGHT;
  const maxTextInRows = cell === 'Crosstab-View' ? 30 : MAX_ROW_TEXT_NUM;
  if (index !== 0) {
    const rowLengthArr = [];
    const isRowLabelUpdated = headerVal.isRowLabelUpdated
      ? headerVal.isRowLabelUpdated
      : false;
    rowLengthArr.push(
      getNumLines(
        partsToShow,
        headerVal,
        'row',
        toggleCheckVal,
        maxTextInRows,
        isRowLabelUpdated,
        cell === 'Crosstab-View'
      )
    );
    const maxlength = Math.max.apply(null, rowLengthArr);

    if (maxlength > 100 && maxlength < 500) {
      finalHeight = maxlength * (MIN_ROW_TEXT_HEIGHT - 2);
    } else if (maxlength > 500) {
      finalHeight = maxlength * (MIN_ROW_TEXT_HEIGHT - 3.6);
    } else {
      finalHeight = maxlength * MIN_ROW_TEXT_HEIGHT;
    }

    finalHeight = finalHeight > minHeight ? finalHeight : minHeight;
  }
  finalHeight = cell === 'cell' ? finalHeight - 1 : finalHeight;
  finalHeight = toggleCheckVal.row ? finalHeight + 20 : finalHeight;
  return `${finalHeight}px`;
};

export const setHeightOfColumns = (
  partsToShow,
  columnHeaderList,
  toggleCheckVal,
  viewName
) => {
  let minheight = MIN_COLUMN_HEIGHT;
  let finalHeight = MIN_COLUMN_HEIGHT;
  let textHeight = MIN_COLUMN_TEXT_HEIGHT;
  let columnTextNum =
    columnHeaderList.length <= 2 ? MAX_COLUMN_TEXT_NUM : MIN_COLUMN_TEXT_NUM;
  let crosstabViewFlag = false;
  if (viewName === 'Crosstab-First-Column') {
    minheight = CROSSTAB_COLUMN_MIN_HEIGHT - 40;
    crosstabViewFlag = true;
  } else if (viewName === 'Crosstab-Column') {
    minheight = CROSSTAB_COLUMN_MIN_HEIGHT;
    crosstabViewFlag = true;
  }

  if (crosstabViewFlag) {
    finalHeight = CROSSTAB_COLUMN_MIN_HEIGHT;
    textHeight = CROSSTAB_COLUMN_TEXT_HEIGHT;
    if (columnHeaderList.length > 3) {
      columnTextNum = 29;
    } else if (columnHeaderList.length === 3) {
      columnTextNum = 43;
    }
  }
  columnTextNum =
    columnHeaderList.length <= 2 ? MAX_COLUMN_TEXT_NUM : columnTextNum;

  const colLengthArr = columnHeaderList.map(col => {
    const isColumnLabelUpdated = col.isColumnLabelUpdated
      ? col.isColumnLabelUpdated
      : false;
    return getNumLines(
      partsToShow,
      col,
      'column',
      toggleCheckVal,
      columnTextNum,
      isColumnLabelUpdated
    );
  });

  const maxlength = Math.max.apply(null, colLengthArr);
  finalHeight = maxlength * textHeight;
  if (viewName === 'Crosstab-First-Column') {
    finalHeight -= 40;
  }
  finalHeight = finalHeight > minheight ? finalHeight : minheight;
  return `${finalHeight}px`;
};

export const createDataSmartSearch = (
  CrosstabBreadCrumbsList,
  DictionaryDetails,
  expression,
  label,
  gridType
) => {
  expression = expression.trim();
  const objSmartSearchArray = {};
  // const expressionFormed = [];
  // const funcExpressionFormed = [];
  const tags = [];
  tags.push(label);

  const operatorArr = [
    '(',
    ')',
    'AND',
    'OR',
    'NOT',
    'XOR',
    'OR NOT',
    'AND NOT',
  ];
  const expArr = getExpressionFormed(expression);
  const isFunctionitem =
    expArr.some(item => functionsInComposer.includes(item.toUpperCase())) ||
    expArr.includes('+');
  const isOperatoritem = expArr.some(
    item =>
      item !== operatorArr[0] &&
      item !== operatorArr[1] &&
      operatorArr.includes(item)
  );
  const isNetAny = isOperatoritem
    ? expArr
        .filter(item => checkKeywords(item))
        .every(item => item.startsWith('XNA'))
    : false;

  const containsGeoNode = expArr.some(item => GEOGRAPHY_BASES.includes(item));

  const containsNotFunction = expArr.some(
    item => item === AC_OPERATORS.type.not.label
  );

  const containsSpecialChars = expArr.some(elem =>
    specialCharList.includes(elem)
  );

  try {
    if (
      [
        isFunctionitem,
        isOperatoritem,
        isNetAny,
        containsGeoNode,
        containsNotFunction,
        containsSpecialChars,
      ].reduce((a, v = true) => (v ? a + 1 : a), 0) > 1
    ) {
      const expressionFormed = expArr.filter(item => checkKeywords(item));
      const objectsAdded = [
        ...getObjectAdded(expressionFormed, DictionaryDetails[gridType]),
      ];

      objSmartSearchArray.autocodenode = buildACStructureNode([], label);
      objSmartSearchArray.objectsAdded = objectsAdded;
      objSmartSearchArray.expressionFormed = expressionFormed;
      objSmartSearchArray.tags = tags;
    } else if (isFunctionitem || isNetAny) {
      const expressionFormed = expArr.filter(item => checkKeywords(item));
      const objectsAdded = getObjectAdded(
        expressionFormed,
        DictionaryDetails[gridType]
      );

      objSmartSearchArray.objectsAdded = objectsAdded;
      objSmartSearchArray.expressionFormed = expressionFormed;
      objSmartSearchArray.tags = tags;

      objSmartSearchArray.autocodenode = buildFunctionNode(
        DictionaryDetails[gridType],
        expression,
        expressionFormed,
        objectsAdded,
        [true],
        label,
        false
      );
    } else {
      let expressionFormed;
      const objectsAdded = [];

      if (containsGeoNode) {
        expressionFormed = mergeGeographyNodes(expArr);
        objectsAdded.push(
          ...getObjectAdded(expressionFormed, DictionaryDetails[gridType])
        );
        objSmartSearchArray.autocodenode = buildAutoCodeObject(
          expressionFormed,
          objectsAdded,
          DictionaryDetails[gridType],
          true,
          label
        );
      } else if (containsNotFunction) {
        expressionFormed = _.cloneDeep(expArr);
        objectsAdded.push(
          ...getObjectAdded(expArr, DictionaryDetails[gridType])
        );

        objSmartSearchArray.autocodenode = buildAutocodenodeWithNot(
          expArr,
          DictionaryDetails[gridType],
          label
        );
      } else {
        expressionFormed = expArr.filter(item => checkKeywords(item));
        // const containsSpecialChars = expArr.some(elem =>
        //   specialCharList.includes(elem)
        // );
        // eslint-disable-next-line no-unused-expressions
        if (containsSpecialChars) {
          objectsAdded.push(
            ...getObjectAdded(expressionFormed, DictionaryDetails[gridType])
          );
          objSmartSearchArray.autocodenode = buildACStructureNode([], label);
        } else {
          objectsAdded.push(
            ...getObjectAdded(expArr, DictionaryDetails[gridType])
          );
          objSmartSearchArray.autocodenode = buildAutoCodeObject(
            expArr,
            objectsAdded,
            DictionaryDetails[gridType],
            true,
            label
          );
        }
      }
      // const objectsAdded = getObjectAdded(expressionFormed, DictionaryDetails[gridType]);
      objSmartSearchArray.objectsAdded = objectsAdded;
      objSmartSearchArray.expressionFormed = expressionFormed;
      objSmartSearchArray.tags = tags;
    }
    return objSmartSearchArray;
  } catch (error) {
    console.warn(error);
    const expressionFormed = expArr.filter(item => checkKeywords(item));
    const objectsAdded = [
      ...getObjectAdded(expressionFormed, DictionaryDetails[gridType]),
    ];

    objSmartSearchArray.objectsAdded = objectsAdded;
    objSmartSearchArray.expressionFormed = expressionFormed;
    objSmartSearchArray.tags = tags;
    objSmartSearchArray.autocodenode = buildACStructureNode([], label);

    return objSmartSearchArray;
  }
};

export const getCurrentTimeInHHMMSS = () => {
  const date = new Date();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  return `${hours}:${minutes}:${seconds}`;
};

// trend check in row col base valid or not
export const trendValidityCheck = response => {
  const data = [];
  response.map(row => {
    const baseList = row.baseList && row.baseList.filter(res => !res.valid);
    const colList = row.colList && row.colList.filter(res => !res.valid);
    const rowList = row.rowList && row.rowList.filter(res => !res.valid);

    if (baseList.length > 0 || colList.length > 0 || rowList.length > 0) {
      data.push(false);
    }
    return null;
  });
  return data.length > 0;
};

export const trendInvalidList = response => {
  let colList = [];
  let rowList = [];
  let baseList = [];

  const data = [];
  response.forEach(row => {
    const bases = row.baseList && row.baseList.filter(res => !res.valid);
    const cols = row.colList && row.colList.filter(res => !res.valid);
    const rows = row.rowList && row.rowList.filter(res => !res.valid);

    if (bases.length > 0) {
      baseList = [...bases];
    }
    if (cols.length > 0) {
      colList = [...cols];
    }
    if (rows.length > 0) {
      rowList = [...rows];
    }
  });
  if (baseList.length > 0) {
    data.push({ grid: 'Bases', invalidList: baseList });
  }
  if (colList.length > 0) {
    data.push({ grid: 'Columns', invalidList: colList });
  }
  if (rowList.length > 0) {
    data.push({ grid: 'Rows', invalidList: rowList });
  }

  return data;
};

export const dictionaryDetailExp = (expressionList) => {
  let updatedExpList = [];
  updatedExpList = expressionList.map(item => {
    const regex = /^[0-9a-zA-Z]+$/;
    const checkReg = regex.test(item);
    if(!checkReg) {
      const splitedArr = item.split(' ');
      return splitedArr[0];
    } 
      return item;
  })
  return updatedExpList;
}

export const updatedPayload = (data, oldExpList) => {
  let updatedData = {};
  _.forEach (oldExpList, (item) => {
   if (!updatedData[item] && data[item]) {
     const obj = { [item]: data[item] };
     updatedData = { ...updatedData, ...obj };
   }
 })
  return updatedData;
}